import { Card, CardBody, Spinner } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LLMModel } from "@webuild-ai/agent-platform/models/components";
import { useBrand } from "@webuild-ai/react-auth";

import { PageTitle } from "@/components/UI/PageTitle";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { ModelIcon } from "@/components/Chat/ModelIcon";
import { getBestForDescription } from "@/utils/modelDescriptions";
import { logger } from "@/telemetry";

/**
 * Models Overview Page
 * Displays all available AI models grouped by provider with descriptions and capabilities.
 */
export const ModelsPage: React.FC = () => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { platformClient } = useWebuildAPIClient();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!platformClient) return;

      try {
        setIsLoading(true);
        setError(null);
        const availableModels = await platformClient.models.list();

        // Sort models by providerId: openai, anthropic, google
        const providerOrder = ["openai", "anthropic", "google"];

        availableModels.sort((a: LLMModel, b: LLMModel) => {
          const aIndex = providerOrder.indexOf(a.providerId);
          const bIndex = providerOrder.indexOf(b.providerId);

          // If both are in the list, sort by their order
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          // If only a is in the list, it comes first
          if (aIndex !== -1) return -1;
          // If only b is in the list, it comes first
          if (bIndex !== -1) return 1;

          // Otherwise, keep original order
          return 0;
        });

        setModels(availableModels);
      } catch (err) {
        logger.error("Error fetching models", err);
        setError(t("models.errorLoading"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchModels();
  }, [platformClient, t]);

  // Group models by provider
  const groupedModels = models.reduce(
    (acc, model) => {
      const provider = model.providerId || "other";

      if (!acc[provider]) {
        acc[provider] = [];
      }
      acc[provider].push(model);

      return acc;
    },
    {} as Record<string, LLMModel[]>,
  );

  // Provider display names
  const providerNames: Record<string, string> = {
    openai: "OpenAI",
    anthropic: "Anthropic",
    google: "Google",
    other: t("models.otherProviders"),
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-16" data-id="models-loading">
          <Spinner color="primary" size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-danger" data-id="models-error">
          {error}
        </div>
      );
    }

    if (models.length === 0) {
      return (
        <div className="text-center py-8 text-default-500" data-id="models-empty">
          {t("models.noModelsAvailable")}
        </div>
      );
    }

    return (
      <div className="space-y-8" data-id="models-content">
        {Object.entries(groupedModels).map(([provider, providerModels]) => (
          <div key={provider} data-id={`provider-section-${provider}`}>
            <h2 className="text-lg font-semibold mb-4 text-default-700">
              {providerNames[provider] || provider}
            </h2>
            <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {providerModels.map((model) => (
                <Card key={model.id} className="p-2" data-id={`model-card-${model.id}`} shadow="sm">
                  <CardBody className="gap-3">
                    <div className="flex items-center gap-3">
                      <ModelIcon
                        alt={model.name}
                        className="flex-shrink-0"
                        height={32}
                        icon={
                          model.icon?.match(/\.(png|jpe?g|svg)$/)
                            ? `/models/${model.icon}`
                            : model.icon || "solar:cpu-bolt-outline"
                        }
                        width={32}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{model.name}</h3>
                        {model.description && (
                          <p className="text-tiny text-default-500 line-clamp-1">
                            {model.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-divider pt-3">
                      <p className="text-xs font-medium text-default-600 mb-1">{t("Best For")}:</p>
                      <p className="text-small text-default-500 line-clamp-4">
                        {getBestForDescription(model.name, t)}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4" data-id="models-page">
      <PageTitle
        centered
        dataId="models-title"
        icon="solar:cpu-bolt-outline"
        img={`/brands/${brand.prefix || "webuild"}/models.svg`}
        subtitle="models.subtitle"
        title="Models Overview"
      />
      {renderContent()}
    </div>
  );
};
